import { useApolloClient } from '@apollo/client';
import { AnalyticIdentifier, hasStandardErrorCode } from '@common/neo.tax';
import { AlertModalLayout, Button } from '@ui-components/neo.tax';
import { useState } from 'react';

import { GET_TICKETING_CONNECTIONS } from '../../graphql';
import { useDisconnectTicketingToken } from '../../hooks';
import { ToastConfigState, useAnalyticId } from '../../state';
import { TicketingConnectionRow } from '../../utils';
import { ModalContainer } from '../delete-confirmation-layout/delete-confirmation-layout.styled';
import { disconnectConfirmationCopy } from './disconnect-copy';
import { GeneralFormErrorStyled } from './ticketing-connections.styled';

/**
 * A sync in progress is the one failure retrying fixes, so it is the one that says so. Matched on
 * the error code the server attaches rather than on the message, which is the vendor's prose and
 * not a contract.
 */
const disconnectFailureMessage = (error: unknown, provider: string): string =>
  hasStandardErrorCode(error, 'CONFLICT')
    ? 'A sync is running against this connection right now. Try again once it has finished.'
    : `Something unexpected prevented ${provider} from being disconnected. Please try again.`;

export const DisconnectTicketingConnection = ({
  close,
  companyId,
  connection,
}: {
  companyId: string;
  connection: TicketingConnectionRow;
  close: () => void;
}): React.ReactElement => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const confirmBtnId = useAnalyticId(AnalyticIdentifier.DISCONNECT_TICKETING_CONNECTION_CONFIRM_BUTTON);
  const cancelBtnId = useAnalyticId(AnalyticIdentifier.DISCONNECT_TICKETING_CONNECTION_CANCEL_BUTTON);
  const [disconnectTicketingToken, { loading }] = useDisconnectTicketingToken(companyId);
  const apolloClient = useApolloClient();

  const onSubmit = async (): Promise<void> => {
    try {
      await disconnectTicketingToken({ variables: { ticketingTokenId: connection.id } });
      close();
    } catch (error) {
      // Already gone server-side — a concurrent disconnect got there first. That is the outcome the
      // customer asked for, so the list is refreshed and the modal closes, the same as a success.
      // Reporting it as a failure would leave them looking at a row that no longer exists.
      if (hasStandardErrorCode(error, 'NOT_FOUND')) {
        const { results } = apolloClient.refetchQueries({ include: [GET_TICKETING_CONNECTIONS] });
        // A refresh that fails leaves a stale row, which the next page load clears; it is not worth
        // turning a disconnect the customer got into an error they cannot act on.
        await Promise.all(results).catch(() => undefined);
        close();
        return;
      }

      setErrorMessage(disconnectFailureMessage(error, connection.provider));
      ToastConfigState.pushErrorToast(`Could not disconnect ${connection.provider}`, { duration: 'MEDIUM' });
    }
  };

  return (
    <ModalContainer>
      <AlertModalLayout
        title={`Disconnect ${connection.provider} (${connection.method})`}
        copy={disconnectConfirmationCopy(connection)}
        trailingContent={
          <>
            <Button id={cancelBtnId} size="base" variant="white" rounded="regular" onClick={close}>
              Cancel
            </Button>
            <Button
              id={confirmBtnId}
              disabled={loading}
              rounded="regular"
              size="base"
              type="submit"
              variant="rsbry"
              onClick={onSubmit}
            >
              {loading ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </>
        }
      >
        <GeneralFormErrorStyled error={!!errorMessage}>{errorMessage}</GeneralFormErrorStyled>
      </AlertModalLayout>
    </ModalContainer>
  );
};
